// src/components/resources/tools/generators/DataTable/DataTableGenerator.tsx

'use client'

import { useCallback, useMemo, useRef, useState } from 'react'
import { Download, Plus, RefreshCw, Trash2, ChevronDown, ChevronUp, Settings, Eye } from 'lucide-react'
import { clsx } from 'clsx'
import { toast } from 'sonner'
import { sendResourceEvent } from '~/lib/gtag'
import { downloadCsv } from '~/lib/utils/csv'
import { formatDate } from '~/lib/utils/date'
import { captureAndDownload } from '../shared/exportImage'
import { waitForStable } from '../shared/waitForStable'
import { ExportButton } from '../shared/ExportButton'
import { useExport } from '../shared/useExport'
import { usePreviewDeferral, useStaleRef } from '../shared/usePreviewDeferral'

type ColumnType = 'text' | 'number' | 'currency' | 'percent' | 'date' | 'status'

interface TableColumn {
  id: string
  name: string
  type: ColumnType
  width: number
}

interface TableConfig {
  title: string
  rowCount: number
  striped: boolean
  bordered: boolean
  compact: boolean
  accentColor: string
  columns: TableColumn[]
  seed: number
}

const defaultColumns: TableColumn[] = [
  { id: 'metric', name: 'Metric', type: 'text', width: 180 },
  { id: 'target', name: 'Target', type: 'percent', width: 120 },
  { id: 'actual', name: 'Actual', type: 'number', width: 120 },
  { id: 'status', name: 'Status', type: 'status', width: 120 },
]

const DEFAULT_CONFIG: TableConfig = {
  title: 'Campaign Performance Snapshot',
  rowCount: 6,
  striped: true,
  bordered: true,
  compact: false,
  accentColor: '#6366f1',
  columns: defaultColumns,
  seed: 42,
}

const columnTypeLabels: Record<ColumnType, string> = {
  text: 'Text',
  number: 'Number',
  currency: 'Currency',
  percent: 'Percent',
  date: 'Date',
  status: 'Status chip',
}

const sampleMetrics = ['CTR', 'CPL', 'CAC', 'ROAS', 'Sessions', 'Pipeline', 'Conversion Rate', 'Bounce Rate', 'Engagement', 'Revenue']

/**
 * Generates a seeded random number between 0 and 1
 * @param seed - The seed value for consistent randomization
 * @param index - Additional index for variation
 * @returns A pseudo-random number between 0 and 1
 */
function seededRandom(seed: number, index: number): number {
  return Math.abs(Math.sin(seed * 9301 + index * 49297) * 233280) % 1
}

/**
 * Generates a mock value for a table cell based on column type
 * @param column - The column configuration
 * @param rowIndex - The row index for seeded randomization
 * @param seed - The seed value for consistent data generation
 * @returns A formatted string value appropriate for the column type
 */
function generateValue(column: TableColumn, rowIndex: number, seed: number): string {
  const random = seededRandom(seed + rowIndex, rowIndex * 17 + column.width)
  switch (column.type) {
    case 'number':
      return Math.round(random * 1000).toLocaleString('en-US')
    case 'currency':
      return `$${(random * 9500 + 500).toFixed(0)}`
    case 'percent':
      return `${(random * 40 + 60).toFixed(1)}%`
    case 'date': {
      // Deterministic sample date: offset days from a fixed epoch using UTC math
      // and UTC-pinned formatting (was local-tz date math + system locale), so the
      // generated table is identical across server/client and build environments.
      const baseDate = new Date('2025-01-01T00:00:00Z')
      baseDate.setUTCDate(baseDate.getUTCDate() + Math.floor(random * 60))
      return formatDate(baseDate, 'shortMonthDay')
    }
    case 'status':
      return random > 0.66 ? 'On track' : random > 0.33 ? 'At risk' : 'Needs review'
    case 'text':
    default:
      return sampleMetrics[rowIndex % sampleMetrics.length] ?? 'Metric'
  }
}

/**
 * Table preview component that renders the generated data table
 * Optimized for mobile with horizontal scrolling and responsive design
 */
function TablePreview({ config }: { config: TableConfig }): JSX.Element {
  const rows = useMemo(() => {
    return Array.from({ length: config.rowCount }, (_, rowIndex) =>
      config.columns.map(column => generateValue(column, rowIndex, config.seed))
    )
  }, [config.columns, config.rowCount, config.seed])

  return (
    <div className="w-full overflow-hidden rounded-3xl border border-slate-800/40 bg-slate-950/80 p-8 shadow-[0_35px_90px_-30px_rgba(15,23,42,0.8)]">
      <header className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <h2 className="text-xl font-semibold text-white">{config.title}</h2>
        <span className="text-xs uppercase tracking-wide text-slate-400">Auto-generated dataset • {config.rowCount} rows</span>
      </header>
      <div className="overflow-x-auto rounded-2xl border border-slate-800">
        <table className={clsx('min-w-full text-sm text-left text-slate-300', config.compact ? 'table-fixed' : 'table-auto')}>
          <thead style={{ backgroundColor: `${config.accentColor}1A`, color: config.accentColor }}>
            <tr>
              {config.columns.map(column => (
                <th key={column.id} className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-300" style={{ minWidth: column.width }}>
                  {column.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={clsx(
                  config.striped && rowIndex % 2 === 0 ? 'bg-slate-900/60' : 'bg-slate-950/40',
                  config.bordered && 'border-t border-slate-800'
                )}
              >
                {row.map((value, columnIndex) => {
                  const column = config.columns[columnIndex]
                  if (!column) return null
                  
                  if (column.type === 'status') {
                    const color = value === 'On track' ? '#22c55e' : value === 'At risk' ? '#f59e0b' : '#f97316'
                    return (
                      <td key={column.id} className="px-4 py-3">
                        <span
                          className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium text-white"
                          style={{ backgroundColor: `${color}33`, color }}
                        >
                          <span className="mr-2 h-2 w-2 rounded-full" style={{ backgroundColor: color }} />
                          {value}
                        </span>
                      </td>
                    )
                  }

                  return (
                    <td key={column.id} className="px-4 py-3 text-sm text-slate-200">
                      {value}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

/**
 * Mobile-optimized tabs component for switching between preview and configuration
 */
function MobileTabs({ 
  activeTab, 
  onTabChange 
}: { 
  activeTab: 'preview' | 'config'
  onTabChange: (tab: 'preview' | 'config') => void
}): JSX.Element {
  return (
    <div className="flex rounded-lg bg-slate-100 p-1 dark:bg-slate-800 md:hidden">
      <button
        type="button"
        onClick={() => onTabChange('preview')}
        className={clsx(
          'flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          activeTab === 'preview'
            ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white'
            : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
        )}
      >
        <Eye className="h-4 w-4" />
        Preview
      </button>
      <button
        type="button"
        onClick={() => onTabChange('config')}
        className={clsx(
          'flex flex-1 items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
          activeTab === 'config'
            ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-700 dark:text-white'
            : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
        )}
      >
        <Settings className="h-4 w-4" />
        Settings
      </button>
    </div>
  )
}

/**
 * Collapsible section component for mobile-friendly configuration panels
 */
function CollapsibleSection({ 
  title, 
  children, 
  defaultOpen = false 
}: { 
  title: string
  children: React.ReactNode
  defaultOpen?: boolean
}): JSX.Element {
  const [isOpen, setIsOpen] = useState(defaultOpen)

  return (
    <section className="space-y-3">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
        aria-expanded={isOpen}
      >
        <span>{title}</span>
        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
      </button>
      {isOpen && (
        <div className="space-y-3">
          {children}
        </div>
      )}
    </section>
  )
}

/**
 * Quick actions component for common operations
 */
function QuickActions({
  onRegenerate,
  onExport,
  onExportPng,
  isExporting,
}: {
  onRegenerate: () => void
  onExport: () => void
  onExportPng: () => void
  isExporting: boolean
}): JSX.Element {
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={onRegenerate}
        className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
      >
        <RefreshCw className="h-4 w-4" />
        Regenerate
      </button>
      <ExportButton
        isExporting={isExporting}
        onExport={onExportPng}
        icon={<Download className="h-4 w-4" />}
        label="PNG"
        exportingLabel="PNG…"
        className="flex-1 rounded-lg bg-slate-900 px-3 py-2 text-sm font-medium text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
      />
      <button
        type="button"
        onClick={onExport}
        className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
      >
        <Download className="h-4 w-4" />
        CSV
      </button>
    </div>
  )
}

/**
 * Main DataTable Generator component
 * Provides a mobile-optimized UI for configuring and generating mock data tables with export functionality
 */
export function DataTableGenerator(): JSX.Element {
  const [config, setConfig] = useState<TableConfig>(DEFAULT_CONFIG)
  const [activeTab, setActiveTab] = useState<'preview' | 'config'>('preview')

  // Render the heavy <TablePreview> (and its per-cell `rows` useMemo) from a
  // `useDeferredValue` snapshot so control keystrokes commit immediately while
  // the table re-rasterizes at a lower priority. Export waits for the snapshot
  // to converge so the PNG matches the live controls.
  const { deferred: deferredConfig, isStale } = usePreviewDeferral(config)
  const previewStaleRef = useStaleRef(isStale)

  // Ref to the styled table preview, captured by the PNG export.
  const previewRef = useRef<HTMLDivElement>(null)

  // Shared export lifecycle for the PNG export: warms html2canvas on mount, a
  // synchronous re-entrancy guard, and the busy flag for the Download button.
  // (CSV export is instant and needs none of this.)
  const { isExporting, runExport } = useExport()

  /**
   * Adds a new column to the table configuration
   */
  const addColumn = useCallback((): void => {
    setConfig(prev => ({
      ...prev,
      columns: [
        ...prev.columns,
        {
          id: `column-${Date.now()}`,
          name: 'New column',
          type: 'text',
          width: 160,
        },
      ],
    }))
  }, [])

  /**
   * Updates a specific column's properties
   */
  const updateColumn = useCallback((id: string, updates: Partial<TableColumn>): void => {
    setConfig(prev => ({
      ...prev,
      columns: prev.columns.map(column => (column.id === id ? { ...column, ...updates } : column)),
    }))
  }, [])

  /**
   * Removes a column from the table configuration
   */
  const removeColumn = useCallback((id: string): void => {
    setConfig(prev => ({
      ...prev,
      columns: prev.columns.filter(column => column.id !== id),
    }))
  }, [])

  /**
   * Regenerates the sample data with a new seed
   */
  const regenerate = useCallback((): void => {
    setConfig(prev => ({ ...prev, seed: prev.seed + 13 }))
  }, [])

  /**
   * Exports the generated data as a CSV file
   */
  const exportCsv = useCallback((): void => {
    const rows = Array.from({ length: config.rowCount }, (_, rowIndex) =>
      config.columns.map(column => generateValue(column, rowIndex, config.seed))
    )
    const header = config.columns.map(column => column.name)
    // Shared serializer: RFC-4180 escaping (only quote when needed), CRLF line
    // endings, and a UTF-8 BOM so non-ASCII sample data opens correctly in Excel.
    downloadCsv(`${config.title.replace(/\s+/g, '-').toLowerCase()}-mock-data`, [header, ...rows])

    sendResourceEvent('complete', {
      resourceType: 'tool',
      resourceSlug: 'data-table-generator',
      params: { tool_type: 'generator', export_format: 'csv' },
    })
  }, [config])

  /**
   * Captures the styled table preview as a retina-density PNG and
   * triggers a download — the visual deck asset to complement the
   * raw-data CSV export. Mirrors the html2canvas export shape used by
   * the mockup generators.
   */
  const exportPng = useCallback((): Promise<void> => runExport(async beginCapture => {
    // On mobile the preview panel is `display:none` while the Settings tab
    // is active (`hidden md:flex`), so html2canvas would capture a 0×0 node
    // and the export would fail. Switch to the preview tab and wait for
    // layout before capturing. Harmless on desktop, where the preview is
    // always visible and the tabs are hidden.
    setActiveTab('preview')
    await new Promise<void>(resolve =>
      requestAnimationFrame(() => requestAnimationFrame(() => resolve())),
    )
    if (!previewRef.current) return
    // The preview renders from `deferredConfig`; wait for that snapshot to
    // converge so the captured PNG matches the live controls, not a stale frame.
    // Fail loudly if it can't settle within the ceiling rather than export stale.
    const stabilized = await waitForStable(() => !previewStaleRef.current)
    if (!stabilized) {
      toast.error('Preview is still updating — pause a moment, then export again.')
      return
    }
    beginCapture()
    await captureAndDownload({
      element: previewRef.current,
      format: 'png',
      filename: `${config.title.replace(/\s+/g, '-').toLowerCase()}-data-table`,
      trackingSlug: 'data-table-generator',
    })
  }), [runExport, config.title, previewStaleRef])

  // Memoize the preview ELEMENT so the per-keystroke urgent re-render
  // (deferredConfig referentially unchanged) reuses it and React bails out of
  // re-rendering the heavy <TablePreview> — it then renders only on the
  // low-priority deferred pass. Without this, useDeferredValue double-renders the
  // table and *raises* INP.
  const tablePreview = useMemo(() => <TablePreview config={deferredConfig} />, [deferredConfig])

  return (
    <div className="w-full space-y-4">
      {/* Mobile tabs */}
      <MobileTabs activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Quick actions - always visible on mobile */}
      <div className="md:hidden">
        <QuickActions onRegenerate={regenerate} onExport={exportCsv} onExportPng={() => void exportPng()} isExporting={isExporting} />
      </div>

      <div className="flex w-full flex-col gap-6 lg:flex-row lg:gap-10">
        {/* Configuration Panel */}
        <aside className={clsx(
          'w-full space-y-4 rounded-xl border border-slate-200 bg-white/80 p-4 shadow-xl backdrop-blur dark:border-slate-800 dark:bg-slate-900/70 lg:w-[380px]',
          activeTab === 'config' ? 'block' : 'hidden md:block'
        )}>
          {/* Basic Settings */}
          <div className="space-y-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-slate-700 dark:text-slate-200">Table title</span>
              <input
                type="text"
                value={config.title}
                onChange={event => setConfig(prev => ({ ...prev, title: event.target.value }))}
                className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
              />
            </label>
            <div className="grid grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-slate-700 dark:text-slate-200">Rows</span>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={config.rowCount}
                  onChange={event => setConfig(prev => ({ ...prev, rowCount: Number.parseInt(event.target.value, 10) || prev.rowCount }))}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                />
              </label>
              <label className="flex flex-col gap-1 text-sm">
                <span className="font-medium text-slate-700 dark:text-slate-200">Color</span>
                <input
                  type="color"
                  value={config.accentColor}
                  onChange={event => setConfig(prev => ({ ...prev, accentColor: event.target.value }))}
                  className="h-10 w-full rounded-md border border-slate-300 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900"
                />
              </label>
            </div>
            <div className="flex flex-wrap gap-3 text-sm">
              <label className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={config.striped}
                  onChange={event => setConfig(prev => ({ ...prev, striped: event.target.checked }))}
                  className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 dark:border-slate-600 dark:text-indigo-500 dark:focus:ring-indigo-500"
                />
                <span className="text-sm">Striped</span>
              </label>
              <label className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={config.bordered}
                  onChange={event => setConfig(prev => ({ ...prev, bordered: event.target.checked }))}
                  className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 dark:border-slate-600 dark:text-indigo-500 dark:focus:ring-indigo-500"
                />
                <span className="text-sm">Borders</span>
              </label>
              <label className="flex items-center gap-2 text-slate-700 dark:text-slate-200">
                <input
                  type="checkbox"
                  checked={config.compact}
                  onChange={event => setConfig(prev => ({ ...prev, compact: event.target.checked }))}
                  className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-900 dark:border-slate-600 dark:text-indigo-500 dark:focus:ring-indigo-500"
                />
                <span className="text-sm">Compact</span>
              </label>
            </div>
          </div>

          {/* Columns Section */}
          <CollapsibleSection title={`Columns (${config.columns.length})`} defaultOpen>
            <button
              type="button"
              onClick={addColumn}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:bg-indigo-500 dark:hover:bg-indigo-400"
            >
              <Plus className="h-4 w-4" />
              Add column
            </button>
            <div className="space-y-3">
              {config.columns.map(column => (
                <div key={column.id} className="rounded-lg border border-slate-200 bg-white px-3 py-3 text-sm shadow-sm dark:border-slate-700 dark:bg-slate-900">
                  <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-slate-400">
                    <span className="truncate">{column.name || 'Column'}</span>
                    {config.columns.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeColumn(column.id)}
                        className="ml-2 flex-shrink-0 text-slate-400 transition hover:text-slate-600 dark:hover:text-slate-200"
                        aria-label="Remove column"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="mt-3 space-y-3">
                    <label className="flex flex-col gap-1 text-xs">
                      <span className="font-medium text-slate-600 dark:text-slate-300">Name</span>
                      <input
                        type="text"
                        value={column.name}
                        onChange={event => updateColumn(column.id, { name: event.target.value })}
                        className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                      />
                    </label>
                    <div className="grid grid-cols-2 gap-3">
                      <label className="flex flex-col gap-1 text-xs">
                        <span className="font-medium text-slate-600 dark:text-slate-300">Type</span>
                        <select
                          value={column.type}
                          onChange={event => updateColumn(column.id, { type: event.target.value as ColumnType })}
                          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                        >
                          {(Object.keys(columnTypeLabels) as ColumnType[]).map(type => (
                            <option key={type} value={type}>
                              {columnTypeLabels[type]}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="flex flex-col gap-1 text-xs">
                        <span className="font-medium text-slate-600 dark:text-slate-300">Width</span>
                        <input
                          type="number"
                          min={80}
                          max={400}
                          value={column.width}
                          onChange={event => updateColumn(column.id, { width: Number.parseInt(event.target.value, 10) || column.width })}
                          className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                        />
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleSection>

          {/* Desktop Actions */}
          <div className="hidden space-y-3 md:block">
            <button
              type="button"
              onClick={regenerate}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
            >
              <RefreshCw className="h-4 w-4" />
              Regenerate data
            </button>
            <ExportButton
              isExporting={isExporting}
              onExport={() => void exportPng()}
              icon={<Download className="h-4 w-4" />}
              className="w-full rounded-lg bg-slate-900 px-4 py-3 text-sm font-semibold text-white shadow-lg transition hover:bg-slate-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
            />
            <button
              type="button"
              onClick={exportCsv}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
          </div>
        </aside>

        {/* Preview Panel */}
        <div className={clsx(
          'flex flex-1 items-center justify-center rounded-xl border border-white/10 bg-gradient-to-br from-slate-900 via-slate-950 to-black p-2 md:rounded-2xl md:p-4',
          activeTab === 'preview' ? 'block' : 'hidden md:flex'
        )}>
          <div ref={previewRef}>
            {tablePreview}
          </div>
        </div>
      </div>
    </div>
  )
}
